import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Container, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f7f9]">
      {/* Blue top strip */}
      <div className="topbar" data-testid="login-topbar">
        <div className="flex items-center gap-2 pl-1">
          <div className="w-7 h-7 rounded-md bg-white/15 flex items-center justify-center">
            <Container className="w-4 h-4 text-white" strokeWidth={2.2} />
          </div>
          <div className="leading-tight">
            <p className="text-[13px] font-semibold tracking-tight">CERF</p>
            <p className="text-[10px] uppercase tracking-widest text-white/70 -mt-0.5">LeadFlow</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pt-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-8 shadow-sm"
          data-testid="login-form"
        >
          <div className="flex items-center gap-2 mb-2 text-[11px] text-slate-500 uppercase tracking-widest">
            <ShieldCheck className="w-3.5 h-3.5 text-[#1D63ED]" /> Secure Sign In
          </div>
          <h2 className="text-2xl font-semibold mb-1 text-slate-900">Welcome back</h2>
          <p className="text-slate-500 text-[13px] mb-6">
            Use the credentials issued by your SuperAdmin.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700 text-xs font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@cerf.com"
                className="mt-1.5 h-9 border-slate-200 focus:border-[#1D63ED] focus:ring-[#1D63ED]/20"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 text-xs font-medium">
                Password / Token
              </Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className="mt-1.5 h-9 border-slate-200 focus:border-[#1D63ED] focus:ring-[#1D63ED]/20"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-9 bg-[#1D63ED] hover:bg-[#1854D9] text-white font-medium rounded-md"
              data-testid="login-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100">
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Only SuperAdmin can create accounts. Contact them to receive your login token.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
